import React, { FC } from 'react';
import { Alert, AlertTitle, Box, Button, Collapse } from '@mui/material';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { TransitionGroup } from 'react-transition-group';
import { UserNotificationDto, useUserContextControllerRetrieveUserContextQuery } from '@services';
import styles from './NotificationsWrapperComponent.module.scss';
import { selectIsSsoUser, selectIsUserMfaSetup } from '../../auth/authSlice';
import manageMfaThunk from '../../auth/manageMfaThunk';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';

interface NotificationsWrapperComponentProps {
}

const NotificationsWrapperComponent: FC<NotificationsWrapperComponentProps> = () => {
    const { data: userContext } = useUserContextControllerRetrieveUserContextQuery(undefined, { pollingInterval: 300_000 });
    const location = useLocation();

    const isOtpEnrolled = useAppSelector(selectIsUserMfaSetup);
    const isSsoUser = useAppSelector(selectIsSsoUser);
    const dispatch = useAppDispatch();

    const hasNotification = userContext?.notifications?.length && userContext.notifications.length > 0;

    const additionalFilterFunctions: { [key: string]: () => boolean } = {
        requireOtpMfa: () => isOtpEnrolled,
        requireNoSso: () => isSsoUser,
    };

    const actions: { [key: string]: () => void } = {
        manageMfa: () => dispatch(manageMfaThunk()),
    };

    const getSeverity = (notification: UserNotificationDto) => notification.severity?.toLowerCase() as 'error' | 'warning' | 'info' | 'success' | undefined;

    const isCustomFiltered = (notification: UserNotificationDto) => {
        const functions = notification.filterFunction?.split(';');
        const isFiltered = functions?.some((f) => {
            if (additionalFilterFunctions[f.replace(';', '').trim()]) {
                const filterValue = additionalFilterFunctions[f]();
                return filterValue;
            }

            return !!f;
        });
        return isFiltered;
    };

    const filterNotification = (notification: UserNotificationDto) => {
        if (notification.from && moment().isBefore(moment.utc(notification.from))) {
            return false;
        }
        if (notification.to && moment().isAfter(moment.utc(notification.to))) {
            return false;
        }
        if (notification.filterFunction && isCustomFiltered(notification)) {
            return false;
        }
        return !(notification.path && !location.pathname.toLowerCase().startsWith(notification.path.toLowerCase()));
    };

    const getAction = (notification: UserNotificationDto) => {
        const { action } = notification;
        if (action && actions[action]) {
            return (
                <Button
                    variant="text"
                    color={getSeverity(notification)}
                    size="small"
                    onClick={() => actions[action]()}
                >
                    {notification.actionText || 'Action'}
                </Button>
            );
        }

        if (action) {
            return (
                <Button
                    variant="text"
                    color={getSeverity(notification)}
                    size="small"
                    href={action}
                    target="_blank"
                    rel="noreferrer"
                >
                    {notification.actionText || 'Ok'}
                </Button>
            );
        }
        return undefined;
    };

    if (!hasNotification) {
        return null;
    }

    return (
        <Box
            className={styles.NotificationsWrapperComponent}
            data-testid="NotificationsWrapperComponent"
            sx={{ mt: 1 }}
        >
            <TransitionGroup>
                {userContext?.notifications?.filter((n) => filterNotification(n)).map((notification) => (
                    <Collapse key={notification.id}>
                        <Alert
                            key={notification.id}
                            severity={getSeverity(notification)}
                            sx={{ display: 'flex', mt: 1 }}
                            action={getAction(notification)}
                        >
                            <Box>
                                {notification.header && (<AlertTitle>{notification.header}</AlertTitle>)}
                                {notification.detail && (notification.detail)}
                            </Box>
                        </Alert>
                    </Collapse>
                ))}
            </TransitionGroup>
        </Box>
    );
};

export default NotificationsWrapperComponent;
